import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';

import { vpnStatusAPI } from '../services/api';

const errText = (err, fallback) => err.response?.data?.detail || fallback;
const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '-');
const STATUS = {
  up: { label: 'Up', color: 'success' },
  degraded: { label: 'One tunnel down', color: 'warning' },
  down: { label: 'Down', color: 'error' },
  unavailable: { label: 'Unavailable', color: 'default' },
};

function Tunnel({ tunnel }) {
  if (!tunnel) return <Typography variant="body2" color="text.secondary">-</Typography>;
  const up = tunnel.status === 'UP';
  const tip = `${tunnel.ip} - ${up ? 'up' : 'down'} since ${fmtTime(tunnel.last_change)}${tunnel.message ? ` - ${tunnel.message}` : ''}`;
  return (
    <Tooltip title={tip}>
      <Chip size="small" variant={up ? 'filled' : 'outlined'} color={up ? 'success' : 'error'} label={up ? 'UP' : 'DOWN'} />
    </Tooltip>
  );
}

// A customer's site-to-site VPN connections, read live from AWS (each has two tunnels)
function VpnPanel({ customerCode }) {
  const [vpns, setVpns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setVpns(null);
    try { setVpns((await vpnStatusAPI.get(customerCode)).data.vpns); }
    catch (err) { setError(errText(err, 'Could not load VPN status')); }
    finally { setLoading(false); }
  }, [customerCode]);

  useEffect(() => { load(); }, [load]);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>VPN connections</Typography>
      {loading && <CircularProgress size={20} />}
      {error && <Alert severity="error">{error}</Alert>}
      {vpns && vpns.length === 0 && <Typography variant="body2" color="text.secondary">No VPN connections are named for {customerCode}.</Typography>}
      {vpns && vpns.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>VPN</TableCell>
                <TableCell>Region</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Tunnel 1</TableCell>
                <TableCell>Tunnel 2</TableCell>
                <TableCell>State changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vpns.map((v) => {
                const s = STATUS[v.status] || STATUS.unavailable;
                const changed = v.tunnels.map((t) => t.last_change).filter(Boolean).sort().pop();
                return (
                  <TableRow key={v.id} hover>
                    <TableCell><Typography variant="body2" fontWeight="bold">{v.name}</Typography><Typography variant="caption" color="text.secondary">{v.id}</Typography></TableCell>
                    <TableCell>{v.location}</TableCell>
                    <TableCell><Chip size="small" color={s.color} label={s.label} /></TableCell>
                    <TableCell><Tunnel tunnel={v.tunnels[0]} /></TableCell>
                    <TableCell><Tunnel tunnel={v.tunnels[1]} /></TableCell>
                    <TableCell>{fmtTime(changed)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {vpns && vpns.length > 0 && <Typography variant="caption" color="text.secondary">Hover a tunnel for its address and how long it has been in that state. With one tunnel down traffic still flows, but there is no backup.</Typography>}
    </Paper>
  );
}

export default VpnPanel;
